import { FC } from 'react'
import styles from '../companyProfile.module.scss'
import { PRIORITY_COLUMNS } from '../types'

interface PriorityGridProps {
  onOtherChange: (value: string) => void
  // Emits the name that was hit, not the resulting list: two clicks inside one
  // React batch would both compute their result from the same stale prop and
  // the first would be lost.
  onToggle: (name: string) => void
  otherValue: string
  selected: string[]
}

const CheckMark: FC = () => (
  <svg fill="none" height="12" viewBox="0 0 12 12" width="12">
    <path
      d="M2.5 6.2L4.8 8.5L9.5 3.8"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
    />
  </svg>
)

// Two columns of 533px, read top-to-bottom, plus an "Other" row under the left
// column that swaps its label for a free-text box.
export const PriorityGrid: FC<PriorityGridProps> = ({
  onOtherChange,
  onToggle,
  otherValue,
  selected,
}) => {
  const toggle = (name: string) => onToggle(name)

  const otherChecked = otherValue.trim() !== ''

  return (
    <div className={styles.priorityGrid}>
      {/* Interleaved so the DOM order matches the visual grid rows. */}
      {PRIORITY_COLUMNS[0].map((left, rowIndex) => {
        const right = PRIORITY_COLUMNS[1][rowIndex]
        return [left, right].map((name) => {
          const isSelected = selected.includes(name)
          return (
            <div
              aria-checked={isSelected}
              className={`${styles.priorityOption} ${
                isSelected ? styles.priorityOptionSelected : ''
              }`}
              key={name}
              onClick={() => toggle(name)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggle(name)
                }
              }}
              role="checkbox"
              tabIndex={0}
            >
              <span
                className={`${styles.checkbox} ${
                  isSelected ? styles.checkboxChecked : ''
                }`}
              >
                {isSelected && <CheckMark />}
              </span>
              <span className={styles.priorityLabel}>{name}</span>
            </div>
          )
        })
      })}

      <div
        className={`${styles.priorityOption} ${
          otherChecked ? styles.priorityOptionSelected : ''
        }`}
      >
        <span
          className={`${styles.checkbox} ${
            otherChecked ? styles.checkboxChecked : ''
          }`}
        >
          {otherChecked && <CheckMark />}
        </span>
        <span className={styles.priorityLabel}>Other</span>
        <input
          className={styles.otherInput}
          onChange={(e) => onOtherChange(e.target.value)}
          placeholder="Type your own..."
          value={otherValue}
        />
      </div>
    </div>
  )
}
